import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, UserPlus } from "lucide-react";
import { toast } from "sonner";

export const UnbookedBanner = ({ date, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/staff/unbooked-visits?date=${date}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [date, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const openDlg = () => { setOpen(true); setQ(""); if (!kids.length) api.get("/staff/children-basic").then((r) => setKids(r.data)); };
  const matches = useMemo(() => { const t = q.trim().toLowerCase(); return t.length < 2 ? [] : kids.filter((k) => k.name.toLowerCase().includes(t) || (k.explorer_id || "").toLowerCase().includes(t)).slice(0, 8); }, [q, kids]);
  const logVisit = async (k) => {
    try { await api.post("/staff/unbooked-visits", { child_id: k.id, date }); toast.success(`${k.name} flagged — the office will book this day`); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mb-4 space-y-2" data-testid="unbooked-banner">
      {rows.length > 0 && (
        <div className="rounded-2xl border border-coral/50 bg-coral/15 p-4" data-testid="unbooked-alert">
          <p className="font-bold text-coral flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{rows.length} explorer{rows.length === 1 ? "" : "s"} here with NO booking — needs booking & payment</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {rows.map((r) => <span key={r.id} className="text-xs bg-white/10 rounded-full px-3 py-1" data-testid={`unbooked-chip-${r.child_id}`}>{r.child_name} · {r.parent_name || "parent"}{r.parent_phone ? ` · ${r.parent_phone}` : ""}</span>)}
          </div>
        </div>
      )}
      <Button size="sm" variant="outline" onClick={openDlg} className="rounded-full bg-transparent border-white/20 text-white/80" data-testid="log-walkin-btn"><UserPlus className="w-4 h-4 mr-1" />Arrived without a booking?</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="walkin-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Flag an unbooked explorer</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Search the explorer who arrived without a booking. The office will see it and book & bill the day.</p>
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or Explorer ID…" className="rounded-xl" data-testid="walkin-search" />
          <div className="space-y-1">
            {matches.map((k) => (
              <button key={k.id} onClick={() => logVisit(k)} className="w-full text-left rounded-xl border border-slate-200 px-3 py-2 hover:border-coral hover:bg-coral/5 transition-colors" data-testid={`walkin-pick-${k.id}`}>
                <span className="font-semibold text-navy">{k.name}</span> <span className="text-xs text-slate-400">{k.explorer_id}{k.school ? ` · ${k.school}` : ""}</span>
              </button>
            ))}
            {q.trim().length >= 2 && !matches.length && <p className="text-sm text-slate-400">No explorer found. New families need to be registered first.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
